/*
 * Colegul AI la închiderea lucrărilor (31 oct 2026, Dan) — întrebarea scurtă dinaintea închiderii.
 *
 * La „Am făcut” / „Finalizează” pe o lucrare simplă sau cu măsurătoare, dacă lipsește dovada minimă (o observație
 * care spune ceva, o valoare citită, o poză), mecanicul primește O SINGURĂ întrebare, legată de lucrare. Răspunsul
 * se scrie pe lucrare. Dacă trece peste, lucrarea se închide oricum — doar că o verifică inginerul. Regula stă pe
 * server (services/coleg_validare.py); aici e doar dialogul. Pe ecran: „Mai e nevoie de o informație”, nimic altceva.
 */
import { api } from './core';

export interface RaspunsColeg {
    intrebare: string;
    raspuns: string;      // gol dacă a trecut peste
    sarit: boolean;
}

interface VerificaCerere {
    interval_id?: number | null;
    machine_id?: string;
    notes?: string;
    poze?: number;
    workflow_id?: number | null;
    parts_used?: string;
}

function _dialog(intrebare: string): Promise<string | null> {
    return new Promise(resolve => {
        const overlay = document.createElement('div');
        overlay.className = 'proc-overlay';
        overlay.innerHTML =
            '<div class="proc-container" style="max-width:480px" role="dialog" aria-modal="true">' +
            '<div class="proc-header"><div class="proc-header-top">' +
            '<h2 class="proc-title">Mai e nevoie de o informație</h2></div></div>' +
            '<div style="padding:16px;display:flex;flex-direction:column;gap:12px">' +
            '<div id="coleg-intrebare" style="font-size:1.05rem;font-weight:600;color:var(--text)"></div>' +
            '<textarea class="dx-input" id="coleg-raspuns" rows="3" style="min-height:80px" ' +
            'placeholder="Scrie pe scurt ce ai văzut sau ce ai citit"></textarea>' +
            '<div style="color:var(--text-muted);font-size:.85rem">Lucrarea se închide oricum. Fără răspuns, o verifică ' +
            'inginerul.</div>' +
            '<div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap">' +
            '<button class="dx-btn-primary" id="coleg-sar" style="background:var(--card-bg);border:1px solid var(--border);' +
            'color:var(--text-muted);padding:12px 22px;min-height:48px">Trec peste</button>' +
            '<button class="dx-btn-primary" id="coleg-ok" style="padding:12px 26px;min-height:48px">Trimite răspunsul</button>' +
            '</div></div></div>';
        document.body.appendChild(overlay);
        overlay.querySelector('#coleg-intrebare')!.textContent = intrebare;
        const camp = overlay.querySelector('#coleg-raspuns') as HTMLTextAreaElement;
        const gata = (v: string | null) => { overlay.remove(); resolve(v); };
        overlay.querySelector('#coleg-ok')!.addEventListener('click', () => {
            const v = camp.value.trim();
            if (!v) { camp.focus(); camp.style.borderColor = 'var(--red)'; return; }
            gata(v);
        });
        overlay.querySelector('#coleg-sar')!.addEventListener('click', () => gata(null));
        setTimeout(() => camp.focus(), 50);
    });
}

/** Întreabă serverul dacă lipsește ceva și, dacă da, pune întrebarea. null = n-a fost nevoie (sau serverul n-a
 *  răspuns — lucrarea nu se blochează niciodată din cauza colegului). */
export async function colegIntreaba(cerere: VerificaCerere): Promise<RaspunsColeg | null> {
    if (!cerere.interval_id && !cerere.workflow_id) return null;
    let r: { nevoie?: boolean; intrebare?: string | null } = {};
    try {
        r = await api('/api/coleg/verifica', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cerere),
        });
    } catch {
        return null;
    }
    if (!r || !r.nevoie || !r.intrebare) return null;
    const raspuns = await _dialog(r.intrebare);
    return { intrebare: r.intrebare, raspuns: raspuns || '', sarit: raspuns === null };
}

/** Nota de trimis, cu răspunsul lipit (dacă există). */
export function notaCuRaspuns(nota: string, c: RaspunsColeg | null): string {
    if (!c || !c.raspuns) return nota;
    return nota ? `${nota}\n${c.raspuns}` : c.raspuns;
}

/** Ce scrie toast-ul după închidere, din răspunsul serverului (strategia v2, 1 nov 2026): lucrarea „în citire” o
 *  citește colegul AI câteva secunde, apoi o validează sau o trimite la inginer — mecanicul nu așteaptă. */
export function colegDupaInchidere(rez: { coleg_decizie?: string | null; validation_status?: string | null } | null,
                                   critic = false): string {
    const d = rez?.coleg_decizie;
    if (d === 'in_citire') return ' — colegul AI citește ce ai scris';
    if (d === 'validat_ai') return ' — validat de colegul AI';
    if (critic || rez?.validation_status === 'pending') return ' — îl verifică inginerul';
    return '';
}
